
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Project } from '@/types';
import { State, Action } from 'vuex-class';
import { errorHandler } from '@/utils';
import { createVersion, updateVersion, deleteVersion, getStaticCacheUrls, createStaticCacheUrl, updateStaticCacheUrl, deleteStaticCacheUrl } from '@/api/services/servers';
import SearchComponent from '@/components/SearchComponent.vue';
import ButtonComponent from '@/components/ButtonComponent.vue';
import ModalComponent from '@/components/ModalComponent.vue';
import SpinnerComponent from '@/components/SpinnerComponent.vue';
import DropdownComponent from '@/components/DropdownComponent.vue';
import InputComponent from '@/components/InputComponent.vue';
import CheckboxComponent from '@/components/CheckboxComponent.vue';

@Component({
  metaInfo: {
    title: 'Модерация версий'
  },
  components: {
    SearchComponent,
    ButtonComponent,
    ModalComponent,
    SpinnerComponent,
    DropdownComponent,
    InputComponent,
    CheckboxComponent
  }
})
export default class VersionsPage extends Vue {
  @State versions: Project.VersionsResponse;
  @Action updateVersionsList;

  query: string = '';
  selectedVersion: Project.VersionForm = new Project.VersionForm();
  isDeleteModalShown: boolean = false;
  isAddModalShown: boolean = false;
  isEditModalShown: boolean = false;
  backendStaticUrls: Project.StaticCacheUrlItem[] = [];
  frontendStaticUrls: Project.StaticCacheUrlItem[] = [];
  selectedStaticUrl: Project.StaticCacheUrlForm = new Project.StaticCacheUrlForm();
  isStaticUrlAddModalShown: boolean = false;
  isStaticUrlEditModalShown: boolean = false;
  isStaticUrlDeleteModalShown: boolean = false;
  isStaticUrlsLoading: boolean = true;

  get filteredVersions() {
    if (this.versions === null || this.versions === undefined) return [];

    const filterFunc = (it: Project.Version) => {
      return it.value.includes(this.query.toLowerCase()) || it.value.split('.').join("").includes(this.query.split('.').join(""))
    }
    const list = Object.keys(this.versions).map(key => {
      return {
        type: key,
        items: this.versions[key].sort((x, y) => x.id > y.id ? 1 : x.id < y.id ? -1 : 0)
      }
    });

    return this.query.length > 0 ? list.filter(group => group.items.some(it => filterFunc(it))).map(group => { return { ...group, items: group.items.filter(it => filterFunc(it)) } }) : list;
  }
  
  get serverTypes() {
    return Object.values(Project.ServerType).map(it => { return { name: it } }).filter(it => it.name != Project.ServerType.PC_MOD)
  }

  openDeleteModal(version: Project.Version) {
    this.selectedVersion = new Project.VersionForm(version);
    this.$nextTick(() => {
      this.isDeleteModalShown = true;
    })
  }
  async removeVersion() {
    this.selectedVersion.isLoading = true;
    try {
      await deleteVersion(this.selectedVersion.id);
      await this.updateVersionsList();
      this.closeModals();
    } catch (error) {
      const errResponse = error && error.data ? error.data : {};
      Object.keys(errResponse).forEach(it => {
        this.selectedVersion.error = errorHandler(it, errResponse[it][0])
      })
    } finally {
      this.selectedVersion.isLoading = false;
    }
  }

  openAddModal() {
    this.selectedVersion = new Project.VersionForm();
    this.$nextTick(() => {
      this.isAddModalShown = true;
    })
  }
  async addVersion() {
    this.selectedVersion.isLoading = true;
    try {
      await createVersion(this.selectedVersion.major.value,
                          this.selectedVersion.minor.value,
                          this.selectedVersion.micro.value,
                          this.selectedVersion.type.name,
                          this.selectedVersion.is_generalizing,
                          this.selectedVersion.is_emphasized,
                          this.selectedVersion.cache_all_servers,
                          this.selectedVersion.cache_pc_classic,
                          this.selectedVersion.cache_pc_mods,
                          this.selectedVersion.cache_bedrock,
                          this.selectedVersion.frontend_cache_all_servers,
                          this.selectedVersion.frontend_cache_pc_mods,
                          this.selectedVersion.frontend_cache_bedrock);
      await this.updateVersionsList();
      this.closeModals();
    } catch (error) {
      const errResponse = error && error.data ? error.data : {};
      Object.keys(errResponse).forEach(it => {
        if (this.selectedVersion[it]) this.selectedVersion[it].error = errorHandler(it, errResponse[it][0]);
        else this.selectedVersion.error = errorHandler(it, errResponse[it][0])
      })
    } finally {
      this.selectedVersion.isLoading = false;
    }
  }

  openEditModal(version: Project.Version) {
    this.selectedVersion = new Project.VersionForm(version);
    this.$nextTick(() => {
      this.isEditModalShown = true;
    })
  }
  async editVersion() {
    this.selectedVersion.isLoading = true;
    try {
      await updateVersion(this.selectedVersion.id,
                          this.selectedVersion.major.value,
                          this.selectedVersion.minor.value,
                          this.selectedVersion.micro.value,
                          this.selectedVersion.type.name,
                          this.selectedVersion.is_generalizing,
                          this.selectedVersion.is_emphasized,
                          this.selectedVersion.cache_all_servers,
                          this.selectedVersion.cache_pc_classic,
                          this.selectedVersion.cache_pc_mods,
                          this.selectedVersion.cache_bedrock,
                          this.selectedVersion.frontend_cache_all_servers ?? false,
                          this.selectedVersion.frontend_cache_pc_mods ?? false,
                          this.selectedVersion.frontend_cache_bedrock ?? false);
      await this.updateVersionsList();
      this.closeModals();
    } catch (error) {
      const errResponse = error && error.data ? error.data : {};
      Object.keys(errResponse).forEach(it => {
        if (this.selectedVersion[it]) this.selectedVersion[it].error = errorHandler(it, errResponse[it][0]);
        else this.selectedVersion.error = errorHandler(it, errResponse[it][0])
      })
    } finally {
      this.selectedVersion.isLoading = false;
    }
  }

  closeModals() {
    this.isDeleteModalShown = false;
    this.isAddModalShown = false;
    this.isEditModalShown = false;

    this.$nextTick(() => {
      this.selectedVersion = new Project.VersionForm();
    })
  }

  openStaticUrlAddModal(urlType: string) {
    this.selectedStaticUrl = new Project.StaticCacheUrlForm();
    this.selectedStaticUrl.url_type = urlType;
    this.$nextTick(() => {
      this.isStaticUrlAddModalShown = true;
    })
  }
  async addStaticUrl() {
    this.selectedStaticUrl.isLoading = true;
    try {
      const item = await createStaticCacheUrl(this.selectedStaticUrl.url.value, this.selectedStaticUrl.url_type);
      if (this.selectedStaticUrl.url_type === 'backend') {
        this.backendStaticUrls.push(item.data);
      } else {
        this.frontendStaticUrls.push(item.data);
      }
      this.closeStaticUrlModals();
    } catch (error) {
      const errResponse = error && error.data ? error.data : {};
      Object.keys(errResponse).forEach(it => {
        if (this.selectedStaticUrl[it]) this.selectedStaticUrl[it].error = errorHandler(it, errResponse[it][0]);
        else this.selectedStaticUrl.error = errorHandler(it, errResponse[it][0])
      })
    } finally {
      this.selectedStaticUrl.isLoading = false;
    }
  }

  openStaticUrlEditModal(item: Project.StaticCacheUrlItem) {
    this.selectedStaticUrl = new Project.StaticCacheUrlForm(item);
    this.$nextTick(() => {
      this.isStaticUrlEditModalShown = true;
    })
  }
  async editStaticUrl() {
    this.selectedStaticUrl.isLoading = true;
    try {
      const item = await updateStaticCacheUrl(this.selectedStaticUrl.id, this.selectedStaticUrl.url.value);
      const list = this.selectedStaticUrl.url_type === 'backend' ? this.backendStaticUrls : this.frontendStaticUrls;
      const edited = list.find(it => it.id === this.selectedStaticUrl.id);
      if (edited) edited.url = item.data.url;
      this.closeStaticUrlModals();
    } catch (error) {
      const errResponse = error && error.data ? error.data : {};
      Object.keys(errResponse).forEach(it => {
        if (this.selectedStaticUrl[it]) this.selectedStaticUrl[it].error = errorHandler(it, errResponse[it][0]);
        else this.selectedStaticUrl.error = errorHandler(it, errResponse[it][0])
      })
    } finally {
      this.selectedStaticUrl.isLoading = false;
    }
  }

  openStaticUrlDeleteModal(item: Project.StaticCacheUrlItem) {
    this.selectedStaticUrl = new Project.StaticCacheUrlForm(item);
    this.$nextTick(() => {
      this.isStaticUrlDeleteModalShown = true;
    })
  }
  async removeStaticUrl() {
    this.selectedStaticUrl.isLoading = true;
    try {
      await deleteStaticCacheUrl(this.selectedStaticUrl.id);
      const list = this.selectedStaticUrl.url_type === 'backend' ? this.backendStaticUrls : this.frontendStaticUrls;
      const removedIndex = list.findIndex(it => it.id === this.selectedStaticUrl.id);
      if (removedIndex !== -1) list.splice(removedIndex, 1);
      this.closeStaticUrlModals();
    } catch (error) {
      const errResponse = error && error.data ? error.data : {};
      Object.keys(errResponse).forEach(it => {
        this.selectedStaticUrl.error = errorHandler(it, errResponse[it][0])
      })
    } finally {
      this.selectedStaticUrl.isLoading = false;
    }
  }

  closeStaticUrlModals() {
    this.isStaticUrlAddModalShown = false;
    this.isStaticUrlEditModalShown = false;
    this.isStaticUrlDeleteModalShown = false;

    this.$nextTick(() => {
      this.selectedStaticUrl = new Project.StaticCacheUrlForm();
    })
  }

  async mounted() {
    try {
      const response = await getStaticCacheUrls();
      const allUrls = response.data;
      this.backendStaticUrls = allUrls.filter(u => u.url_type === 'backend');
      this.frontendStaticUrls = allUrls.filter(u => u.url_type === 'frontend');
    } catch (error) {
      console.log(error);
    } finally {
      this.isStaticUrlsLoading = false;
    }
  }
}
